import { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionType, ComponentType, useLogEventOnMount } from '@cbhq/client-analytics';
import { isInAppBrowser, isWebView } from '@cbhq/idv-sdk-common/utils/mobile';

import { createErrorLoggers } from './legacy/utils/analytics/errorLogger';
import { checkForCamera } from './legacy/utils/mediaDevices';
import type { IdCaptureTokenProps } from './platform/components/Idv/IdCaptureToken';
import type { IdCaptureProps } from './types';

const IdCaptureToken = lazy(() => import('./platform/components/Idv/IdCaptureToken'));

const IdCaptureSDK = lazy(() => import('./legacy/components/IdCaptureSDK'));

const IdCapture = lazy(() => import('./IdCapture'));

export type IdCaptureLegacyProps = IdCaptureProps & {
  isSDKEnabled?: boolean;
  token?: never;
};

export type IdCapturePlatformProps = IdCaptureTokenProps;

export type IdCaptureWrapperProps = IdCapturePlatformProps | IdCaptureLegacyProps;

const omittedAnalyticsKeys = ['token'];

function IdCapturePlatform(props: IdCapturePlatformProps) {
  return <IdCaptureToken {...props} token={props.token} />;
}

function IdCaptureLegacy(props: IdCaptureLegacyProps) {
  const { forceFacematchType, isSDKEnabled, track } = props;
  const [fallbackToSDKCapture, setFallbackToSDKCapture] = useState(false);

  // Can't use useErrorLoggers hook here because it's not within the context yet!
  const errorLoggers = useMemo(() => createErrorLoggers(track), [track]);

  useEffect(() => {
    checkForCamera(track);
  }, [track]);

  // sanitize props to remove functions and other non-serializable values
  // non-serializable values are replaced with `true` to indicate that the value is present
  // null and undefined are included to track when props are explicitly set to these values
  const sanitizedProps = useMemo(() => {
    const filteredProps: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(props).filter(
      ([key]) => !omittedAnalyticsKeys.includes(key),
    )) {
      if (value === undefined || value === null) {
        filteredProps[key] = value;
      } else if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'symbol' ||
        typeof value === 'bigint'
      ) {
        filteredProps[key] = value;
      } else {
        filteredProps[key] = true;
      }
    }

    // sometime referrer is overridden by HTTP referrer header in analytics amplitude, hence using a different key here
    filteredProps.idv_referrer = filteredProps.referrer;

    // Boolean stand-in for the omitted token so funnels can still split token vs
    // legacy without logging the credential itself.
    filteredProps.isTokenFlow = !!props.token;

    // Set after the props loop so a caller-supplied prop can never override the detected values.
    // A WebView (native react-native-webview host, JS bridge present) and an in-app browser
    // (SFSafariViewController / Custom Tabs, UA/query-param based) are mutually exclusive: WebView
    // wins, so the two flags always describe distinct environments.
    const inWebView = isWebView();
    filteredProps.is_webview = inWebView;
    filteredProps.is_in_app_browser = !inWebView && isInAppBrowser();

    if (isSDKEnabled) {
      filteredProps.sdk_vendor = props.featureFlags?.isPersonaSDKEnabled
        ? 'SDK_VENDOR_PERSONA'
        : 'SDK_VENDOR_ONFIDO';
    }

    return filteredProps;
  }, [props, isSDKEnabled]);

  // Can't use useTrackOnMount hook here because it's not within the context yet!
  useLogEventOnMount('id_capture', {
    action: ActionType.render,
    componentType: ComponentType.content_script,
    content: 'init',
    ...sanitizedProps,
  });

  const handleNetworkErrorFallbackToSDKCapture = useCallback(() => {
    setFallbackToSDKCapture(true);
  }, []);

  // If SDK is enabled or we've already fallen back to Onfido, use SDK flow
  if (isSDKEnabled || fallbackToSDKCapture) {
    return <IdCaptureSDK {...props} isSDKEnabled fallbackToSDKCapture={fallbackToSDKCapture} />;
  }

  const hasInvalidFacematchType = forceFacematchType && forceFacematchType !== 'face';

  if (hasInvalidFacematchType) {
    // log error if incorrect forceFacematchType is set
    errorLoggers.idCaptureLogAnomaly({
      name: 'invalidProps',
      message: `invalid value is set for props forceFacematchType - ${forceFacematchType}`,
    });
  }

  return (
    <IdCapture
      {...props}
      forceFacematchType={hasInvalidFacematchType ? undefined : forceFacematchType}
      onNetworkErrorFallbackToSDKCapture={handleNetworkErrorFallbackToSDKCapture}
    />
  );
}

export default function IdCaptureWrapper(props: IdCaptureWrapperProps) {
  if (props.token) {
    return <IdCapturePlatform {...props} />;
  }

  return <IdCaptureLegacy {...(props as IdCaptureLegacyProps)} />;
}
